import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useReactFlow } from '@xyflow/react';
import DataTable from 'src/components/data-table';
import { EditCell } from 'src/components/data-table/action-cell';
import { TableCell } from 'src/components/data-table/table-cell';
import IndeterminateCheckbox from 'src/components/indeterminate-checkbox';
import { IVlanItem } from 'src/context/global/types';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { dispatchNodesChanged } from 'src/utils/dispatch-nodes-changed';
import { v4 } from 'uuid';

import {
  EUntaggedVlan,
  IEdgeExtraData,
  EAllowIncomingPackets,
} from '../edges/types';
import { NodeCategoryModel } from '../models/node-data';
import IpSubnetEditCell from './IpSubnetEditCell';

function VlanTable() {
  const { vlans, setVlans } = useGlobalContext();
  const { setNodes, setEdges, getNodes } = useReactFlow();

  const columnHelper = createColumnHelper<IVlanItem>();

  const maxVlanId =
    vlans.length === 0 ? 0 : Math.max(...vlans.map((vlan) => vlan.vlanId));

  const columns = [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <IndeterminateCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }) => (
        <div className="flex justify-start pl-3">
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        </div>
      ),
      meta: { type: 'checkbox' },
    }),
    columnHelper.accessor('color', {
      header: 'Color',
      cell: TableCell,
      meta: { type: 'color', required: true },
    }),
    columnHelper.accessor('vlanId', {
      header: 'VLAN ID',
      cell: TableCell,
      meta: {
        type: 'number',
        required: true,
        validate: (value: string) => {
          const num = parseInt(value, 10);
          return num > 0 && num < 4096;
        },
        validationMessage: 'VLAN ID must be between 1 and 4095',
      },
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: TableCell,
      meta: {
        type: 'text',
        required: true,
        className: 'block max-w-60 truncate',
      },
    }),
    columnHelper.accessor('ipSubnet', {
      header: 'IP Subnet',
      cell: TableCell,
      meta: {
        type: 'text',
        required: true,
        renderCell({ getValue, row, column, table }) {
          const ipSubnet = getValue();
          const { subnetMask } = row.original;
          return (
            <div>
              {ipSubnet} / {subnetMask}
            </div>
          );
        },
        renderEditCell: (props) => <IpSubnetEditCell {...props} />,
      },
    }),
    columnHelper.display({
      id: 'actions',
      cell: EditCell,
    }),
  ] as ColumnDef<IVlanItem>[];

  return (
    <DataTable<IVlanItem>
      columns={columns}
      data={vlans}
      setData={setVlans}
      enableRowSelection
      newRow={{
        id: v4(),
        color: '#000000',
        vlanId: maxVlanId + 1,
        ip: '',
        subnetMask: '',
        description: 'New VLAN',
      }}
      thClass="py-3.5 pl-4 pr-3 sm:pl-3 text-left"
      // preSaveWarning={(newData, oldData) => {
      //   // check some ip change
      //   const ipChange = newData.some((newRow) => {
      //     const oldRow = oldData.find((row) => row.id === newRow.id);
      //     return oldRow?.ipSubnet && oldRow?.ipSubnet !== newRow.ipSubnet;
      //   });
      //   if (ipChange) {
      //     return 'Changing the IP subnet should remove all device IPs of this VLAN automatically';
      //   }
      //   return '';
      // }}
      onChangeCallback={(newData, oldData) => {
        const ipSubnetOrVlanChanged =
          newData.ipSubnet !== oldData.ipSubnet ||
          newData.vlanId !== oldData.vlanId;
        const subnetMaskOnlyChanged =
          !ipSubnetOrVlanChanged && newData.subnetMask !== oldData.subnetMask;

        if (ipSubnetOrVlanChanged) {
          const affectedIds = getNodes()
            .filter((node) =>
              (node.data?.category as NodeCategoryModel)?.ipInterfaces?.some(
                (i) => i.vlanId.toString() === oldData.vlanId.toString(),
              ),
            )
            .map((n) => n.id);
          setNodes((oldNodes) => {
            let nodeTargetIndex = 1;
            const newNodes = [...oldNodes].map((node) => {
              const nodeIpInterfaces =
                ((node.data?.category as NodeCategoryModel)
                  ?.ipInterfaces as any[]) || [];
              const newIpInterfaces = nodeIpInterfaces.map((ipInterface) => {
                if (
                  ipInterface.vlanId.toString() === oldData.vlanId.toString()
                ) {
                  nodeTargetIndex += 1;

                  const newIp = newData.ipSubnet
                    ? `${newData.ipSubnet
                        ?.split('.')
                        .slice(0, 3)
                        .join('.')}.${nodeTargetIndex}`
                    : '';
                  return {
                    ...ipInterface,
                    ip: newIp,
                    subnetMask: newData.subnetMask,
                    subnet: newData.ipSubnet,
                    gateway: newData.ipSubnet,
                  };
                }
                return ipInterface;
              });

              const newNodeCategory = {
                ...(node?.data?.category as any),
                ipInterfaces: newIpInterfaces,
              };

              return {
                ...node,
                data: {
                  ...node.data,
                  category: newNodeCategory,
                },
              };
            });
            return newNodes;
          });
          dispatchNodesChanged(affectedIds);
        } else if (subnetMaskOnlyChanged) {
          const affectedIds = getNodes()
            .filter((node) =>
              (node.data?.category as NodeCategoryModel)?.ipInterfaces?.some(
                (i) => i.vlanId.toString() === oldData.vlanId.toString(),
              ),
            )
            .map((n) => n.id);
          setNodes((oldNodes) => {
            const newNodes = [...oldNodes].map((node) => {
              const nodeIpInterfaces =
                ((node.data?.category as NodeCategoryModel)
                  ?.ipInterfaces as any[]) || [];
              const newIpInterfaces = nodeIpInterfaces.map((ipInterface) => {
                if (
                  ipInterface.vlanId.toString() === oldData.vlanId.toString()
                ) {
                  return {
                    ...ipInterface,
                    subnetMask: newData.subnetMask,
                  };
                }
                return ipInterface;
              });

              const newNodeCategory = {
                ...(node?.data?.category as any),
                ipInterfaces: newIpInterfaces,
              };

              return {
                ...node,
                data: {
                  ...node.data,
                  category: newNodeCategory,
                },
              };
            });
            return newNodes;
          });
          dispatchNodesChanged(affectedIds);
        }

        if (newData.vlanId.toString() !== oldData.vlanId.toString()) {
          setEdges((oldEdges) =>
            oldEdges.map((edge) => {
              const edgeData = edge.data as IEdgeExtraData;
              if ((edgeData?.vlanIds ?? []).includes(oldData.vlanId)) {
                return {
                  ...edge,
                  data: {
                    ...edge.data,
                    vlanIds: (edge.data?.vlanIds as number[]).map((vlanId) =>
                      vlanId === oldData.vlanId
                        ? parseInt(newData.vlanId.toString(), 10)
                        : parseInt(vlanId.toString(), 10),
                    ),
                    ...(edge.data?.untaggedVlan === oldData.vlanId
                      ? { untaggedVlan: newData.vlanId.toString() }
                      : {}),
                    ...(edge.data?.allowIncomingPackets === oldData.vlanId
                      ? { allowIncomingPackets: newData.vlanId.toString() }
                      : {}),
                  },
                };
              }
              return edge;
            }),
          );
        }
      }}
      onDeletedCallback={(deletedData) => {
        const affectedIds = getNodes()
          .filter((node) =>
            (node.data?.category as NodeCategoryModel)?.ipInterfaces?.some(
              (i) => i.vlanId.toString() === deletedData.vlanId.toString(),
            ),
          )
          .map((n) => n.id);
        setNodes((oldNodes) => {
          const newNodes = [...oldNodes].map((node) => {
            const nodeIpInterfaces =
              ((node.data?.category as NodeCategoryModel)
                ?.ipInterfaces as any[]) || [];
            const newIpInterfaces = nodeIpInterfaces.filter(
              (ipInterface) =>
                deletedData.vlanId.toString() !== ipInterface.vlanId.toString(),
            );
            const newNodeCategory = {
              ...(node?.data?.category as any),
              ipInterfaces: newIpInterfaces,
            };

            return {
              ...node,
              data: {
                ...node.data,
                category: newNodeCategory,
              },
            };
          });
          return newNodes;
        });
        dispatchNodesChanged(affectedIds);
        setEdges((oldEdges) =>
          oldEdges.map((edge) => ({
            ...edge,
            data: {
              ...edge.data,
              vlanIds: (edge.data as IEdgeExtraData)?.vlanIds?.filter(
                (vlanId) => vlanId.toString() !== deletedData.vlanId.toString(),
              ),
              ...(edge.data?.untaggedVlan === deletedData.vlanId
                ? { untaggedVlan: EUntaggedVlan.AUTO }
                : {}),
              ...(edge.data?.allowIncomingPackets === deletedData.vlanId
                ? { allowIncomingPackets: EAllowIncomingPackets.AUTO }
                : {}),
            },
          })),
        );
      }}
      onMultipleDeletedCallback={(deletedData) => {
        const affectedIds = getNodes()
          .filter((node) =>
            (node.data?.category as NodeCategoryModel)?.ipInterfaces?.some(
              (i) =>
                deletedData.some(
                  (d: { vlanId: number }) =>
                    d.vlanId.toString() === i.vlanId.toString(),
                ),
            ),
          )
          .map((n) => n.id);
        setNodes((oldNodes) => {
          const newNodes = [...oldNodes].map((node) => {
            const nodeIpInterfaces =
              ((node.data?.category as NodeCategoryModel)
                ?.ipInterfaces as any[]) || [];
            const newIpInterfaces = nodeIpInterfaces.filter(
              (ipInterface) =>
                !deletedData.some(
                  (deletedItem: any) =>
                    deletedItem.vlanId.toString() ===
                    ipInterface.vlanId.toString(),
                ),
            );

            const newNodeCategory = {
              ...(node?.data?.category as any),
              ipInterfaces: newIpInterfaces,
            };

            return {
              ...node,
              data: {
                ...node.data,
                category: newNodeCategory,
              },
            };
          });
          return newNodes;
        });
        dispatchNodesChanged(affectedIds);
        setEdges((oldEdges) =>
          oldEdges.map((edge) => ({
            ...edge,
            data: {
              ...(edge.data as IEdgeExtraData),
              vlanIds: (edge.data as IEdgeExtraData)?.vlanIds?.filter(
                (vlanId) =>
                  !deletedData.some(
                    (deletedItem: any) =>
                      deletedItem.vlanId.toString() === vlanId.toString(),
                  ),
              ),
              ...(edge.data?.untaggedVlan &&
              deletedData.some(
                (deletedItem: any) =>
                  deletedItem.vlanId.toString() === edge.data?.untaggedVlan,
              )
                ? { untaggedVlan: EUntaggedVlan.AUTO }
                : {}),
              ...(edge.data?.allowIncomingPackets &&
              deletedData.some(
                (deletedItem: any) =>
                  deletedItem.vlanId.toString() ===
                  edge.data?.allowIncomingPackets,
              )
                ? { allowIncomingPackets: EAllowIncomingPackets.AUTO }
                : {}),
            },
          })),
        );
      }}
    />
  );
}

export default VlanTable;
